/*
 * Generated from Empori.Typings.TypescriptTyping
 */

/**
 * A general input item primarily used for @see Empori.Core.Data.Entities.Form.
 */
export interface InputItem {

  /**
   * This identification is used for frontend drag and drop only.
   */
  id: number;

  /**
   * The unique name that is associated with the value.
   */
  uniqueName: string;

  /**
   * Name is used for frontend reference only.
   */
  name: string;

  /**
   * Single value pre-defined by admin or assigned by the end-user.
   */
  value: string;

  /**
   * Multiple values pre-defined by admin or assigned by the end-user.
   */
  values: string[];

  /**
   * A shorter descriptive text about the input.
   */
  label: string;

  /**
   * Type of HTML input.
   */
  type: EmporiInputType;

  /**
   * A descriptive text within the input.
   */
  placeholder: string;

  /**
   * A descriptive text associated to the input.
   */
  description: string;

  /**
   * If the input should be required for form submission.
   */
  required: boolean;

  /**
   * Validation member translates to "pattern" HTML attribute and only works with the following input types: text, date, search, url, tel, email, and password.
   */
  validation: string;

  /**
   * The global title attribute helps describe the pattern to the end user.
   */
  title: string;

  /**
   * Alternatives member is only relevant for select, radio and checkbox input types.
   */
  alternatives: InputAlternative[];

  /**
   * Only relevant for input-type file to specify what filetypes are allowed
   */
  allowedFiles: AllowedFileType[];

  /**
   * If this member is true, form recipient specified in global and individual settings are ignored.
   * The mailto recipient gets taken from the selected "Alternatives" > "MailTo" value.
   * Only one input in each form can have this member set as true.
   */
  controlsRecipients: boolean;

  /**
   * FontAwesome string reference to an icon in their library. Primarily used for back-office frontend.
   */
  icon: string;

  /**
   * How much space this input should take in the horizontal context of the parent element.
   */
  width: WidthLayout;

  /**
   * If this member is true, the associated @see Empori.Core.Controllers.Models.EmporiInputType.Email input will send a confirmation e-mail to the user.
   */
  sendConfirmationEmail: boolean;

  /**
   * If this is true the first option will set as default empty option
   */
  isDefaultEmptyOptionSelected: boolean;
}

/**
 * Various HTML input types.
 */
export enum EmporiInputType {

  /**
   * <input type="text">
   */
  text = 0,

  /**
   * <textarea></textarea>
   */
  textArea = 1,

  /**
   * <input type="email">
   */
  email = 2,

  /**
   * <input type="date">
   */
  date = 3,

  /**
   * <select></select>
   */
  select = 4,

  /**
   * <input type="number">
   */
  number = 5,

  /**
   * <input type="checkbox">
   */
  checkbox = 6,

  /**
   * <input type="radio">
   */
  radiobutton = 7,

  /**
   * <input type="submit">
   */
  submit = 8,

  /**
   * <input type="file">
   */
  file = 9,

  /**
   * Custom HTML within the <form> element.
   */
  rte = 10,

  /**
   * <input type="time">
   */
  time = 11,

  /**
   * <input type="time">
   */
  color = 12
}

/**
 * Multiple input alternatives that holds a @see Empori.Core.Controllers.Models.InputAlternative.Name and a @see Empori.Core.Controllers.Models.InputAlternative.MailTo address if @see Empori.Core.Controllers.Models.InputItem.ControlsRecipients is active for this input.
 */
export interface InputAlternative {

  /**
   * The display name of the alternative that can be selected or viewed.
   */
  name: string;

  /**
   * If the input @see Empori.Core.Controllers.Models.InputItem.ControlsRecipients is used for an input, this value can hold an e-mail address associated to the alternative @see Empori.Core.Controllers.Models.InputAlternative.Name.
   */
  mailTo: string;
}

export interface AllowedFileType {
  type: string;
}

/**
 * A design reference that can be used for width rules.
 */
export enum WidthLayout {

  /**
   * Most commonly used for setting a 50 percentage width to something.
   */
  half = 0,

  /**
   * Most commonly used for setting a 100 percentage width to something.
   */
  full = 1
}

export interface GoogleMapsJson {
  height: number;
  apiKey: string;
  iframeHtml: string;
  initialZoom: number;
  initialLat: number;
  initialLng: number;
  markerCollection: GoogleMapsMarkers;
}

export interface GoogleMapsMarkers {
  markers: Marker[];
}

export interface Marker {
  address: string;
  lat: number;
  lng: number;
  description: string;
}

export interface HiddenTextJson {
  height: number;
  showText: string;
  hideText: string;
}

export interface ButtonsModel {
  buttons: ButtonModel[];
}

export interface ButtonModel {
  text: string;
  url: string;
  iconHtml: string;
  newWindow: boolean;
  displayAsText: boolean;
  inverted: boolean;
  anchor: string;
}

export interface OpenHoursDayItem {
  dayLabel: string;
  openHour: string;
  closeHour: string;
}

export interface SocialLinksModel {
  links: SocialLink[];
}

export interface SocialLink {
  linkIcon: LinkIcon;
  icon: string;
  linkLabel: string;
  linkUrl: string;
  url: string;
}

export enum LinkIcon {
  facebook = 0,
  twitter = 1,
  instagram = 2,
  linkedIn = 3,
  youtube = 4,
  pinterest = 5,
  default = 99
}

export interface FaqModel {
  faqItems: FaqItem[];
}

export interface FaqItem {
  question: string;
  answer: string;
  iconHtml: string;
  enableIconRotate: boolean;
}

export interface ImageGalleryModel {
  imageGalleryEditorModel: ImageGalleryEditorModel;
  delay: number;
  staticHeight: number;
  autoplay: boolean;
  macroId: string;
  spaceBetween?: number;
  slidesPerView?: number;
  fullscreenEnabled: boolean;
}

export interface ImageGalleryEditorModel {
  slides: Slide[];
  extraInfo: GalleryExtraInfoModel;
}

export interface Slide {
  image: string;
  poster: string;
  titleText: string;
  subtitleText: string;
  isImageOverlay: boolean;
  buttonsModel: ButtonsModel;
  contentAlignment: ContentAlignment;
  verticalAlignment: VerticalAlignment;
}

export enum ContentAlignment {
  left = 0,
  center = 1,
  right = 2
}

export enum VerticalAlignment {
  top = 0,
  center = 1,
  bottom = 2
}

export interface GalleryExtraInfoModel {
  isHideExtraInfo: boolean;
  columns: GalleryExtraInfoColumn[];
}

export interface GalleryExtraInfoColumn {
  image: string;
  content: string;
  title: string;
}

export interface CounterJson {
  id: string;
  countFrom: number;
  countTo: number;
  time: number;
  started: boolean;
}

export interface FormDetails {
  formId: string;
  formSubmitButtonId: string;
  form: Form;
  globalMaxUploadFileSize?: number;
  stayOnPage: boolean;
  messageKey: string;
  statusKey: string;
  fetchUrl: string;
  fetchUrlEmporiCaptcha: string;
  spamProtectionDetails: SpamProtectionDetails;
  translations: { [key: string]: string };
}

export interface SpamProtectionDetails {
  isTurnstileEnabled: boolean;
  isEmporiCaptchaEnabled: boolean;
  fetchUrlEmporiCaptcha: string;
  captchaImgId: string;
}

export interface ImageWithInfo {
  imgUrl: string;
  imgUrlOriginal: string;
  imgAlt: string;
  title: string;
  pageLink: string;
}

export enum RowAnimationStyle {
  none = 0,
  fadeUp = 1
}

export interface LogotypesJson {
  mediaObjects: ImageWithInfo[];
  logotypesPerView?: number;
  spaceBetween?: number;
  display: LogotypesDisplay;
  hideNavigation: boolean;
  autoplay: boolean;
  externalLink?: boolean;
  maxHeight?: number;
  filterColor: FilterColor;
  buttonLabel: string;
}

export enum LogotypesDisplay {
  slideshow = 0,
  grid = 1,
  partiallyHiddenGrid = 2
}

export enum FilterColor {
  none = 0,
  black = 1,
  gray = 2,
  white = 3
}

export interface InteractiveImageObj {
  image: string;
  buttons: ButtonObj[];
}

export interface ButtonObj {
  id: string;
  index: number;
  positionX: number;
  positionY: number;
  featureInput: FeatureInput;
}

export interface FeatureInput {
  sortingTitle: string;
  inputText: string;
  active: boolean;
}

export interface BannerBlockEditorModel {
  banners: Banner[];
}

export interface Banner {
  image: string;
  poster: string;
  backgroundColor: string;
  titleText: string;
  subtitleText: string;
  isImageBlur: boolean;
  blurType: BlurType;
  blurHorizontalSide: BlurHorizontalSide;
  blurVerticalSide: BlurVerticalSide;
  blurOption: BlurOption;
  buttonsModel: ButtonsModel;
  clickableBanner: boolean;
  contentAlignment: ContentAlignment;
  verticalAlignment: VerticalAlignment;
}

export enum BlurType {
  vertical = 0,
  horizontal = 1
}

export enum BlurHorizontalSide {
  left = 0,
  right = 1
}

export enum BlurVerticalSide {
  top = 0,
  bottom = 1
}

export enum BlurOption {
  half = 0,
  twoThird = 1,
  oneFourth = 2,
  contentBox = 3
}

export interface UmbracoSearchModel {
  culture: string;
  selectedNodesIdList: number[];
  displayTitleAlias: string;
  displayDescriptionAlias: string;
  pageSize?: number;
  searchAliasItems: SearchAliasItem[];
  placeHolderText: string;
  noResultFoundText: string;
}

export interface SearchAliasItem {
  alias: string;
  fieldDescription: string;
  isRemovedFromSearch: boolean;
  boostValue: string;
  culture: string;
}

export interface PopupViewModel {
  active?: boolean;
  showInterval: number;
  expirationDate?: string;
  appearOnScroll?: number;
  name: string;
}

export interface SearchAliasItemModel {
  searchAliasItems: SearchAliasItem[];
}

export interface BrandModelData {
  title: string;
  year: string;
  link: string;
  lang: string;
}

export interface BrandModelItem {
  name: string;
  year: string;
  description: string;
  pricePrefix: string;
  priceFrom?: number;
  mainImage: string;
  created: string;
  link: string;
  brand: string;
  family: string;
  category: string;
  imageCrop: string;
}

export interface BrandPagesConfig {
  lang: string;
  translations: BrandPagesTranslations;
}

export interface BrandPagesTranslations {
  viewingModelsCount: string;
  filter: string;
  sort: string;
  showingAllBrands: string;
  showingAllFamilies: string;
  showingAllCategories: string;
  closeAndShowModels: string;
  alphabetAsc: string;
  alphabetDesc: string;
  priceAsc: string;
  priceDesc: string;
  createdAsc: string;
  createdDesc: string;
  noModelsFound: string;
}

export interface FormPostResponse {
  posts: FormPost[];
}

export interface FormExportQueryString {
  formId: number;
  culture: string;
  format?: SpreadsheetFormat;
}

/**
 * Supported spreadsheet formats.
 */
export enum SpreadsheetFormat {

  /**
   * Default XML-based file format for Excel 2010 and Excel 2007.
   */
  xlsx = 0,

  /**
   * Excel 97 - Excel 2003 Binary file format (BIFF8).
   */
  xls = 1,

  /**
   * Comma separated values.
   */
  csv = 2,

  /**
   * JavaScript object notation.
   */
  json = 3
}

export interface ResendFormPostsMailResponse {
  failedResentMails: FailedResentMail[];
  succededResentMails: number[];
}

export interface FailedResentMail {
  id: number;
  errorMessage: string;
}

export interface OptionsResponse {
  key: string;
  title: string;
  definitions: ObjectDefinition[];
  properties: OptionsProperty[];
  model: { [key: string]: any };
}

export interface ObjectDefinition {
  type: string;
  propertyEditor: string;
  formatString: string;
  properties: OptionsProperty[];
}

export interface OptionsProperty {
  title: string;
  description: string;
  key: string;
  type: string;
  propertyType: OptionsPropertyType;
  tab: string;
  group: string;
  relation: OptionsRelation;
  isAdminOnly?: boolean;
  isReadOnly?: boolean;
  isVaryByCulture?: boolean;
  evaluateOnChange?: boolean;
  mappingTitle: string;
}

/**
 * Options property type of an object definition.
 */
export enum OptionsPropertyType {

  /**
   * Primitive value such as a number or a string.
   */
  primitive = 0,

  /**
   * Nested object structure.
   */
  object = 1,

  /**
   * Array of nested object structures.
   */
  array = 2
}

export interface OptionsRelation {
  multiSelect?: boolean;
  optional?: boolean;
  style: number;
  values: IOptionsValue[];
}

/**
 * Selectable options relation value.
 */
export interface IOptionsValue {

  /**
   * Title of value.
   */
  title: string;

  /**
   * Options value
   */
  value: string;

  /**
   * Description to show to the editor.
   */
  description: string;

  /**
   * Description to show to the editor.
   */
  isVaryByCulture?: boolean;

  /**
   * Indicate that this value is selected.
   */
  selected?: boolean;

  /**
   * Associate a context to this value. Can be used as a reference linked against this value.
   * 
   * <para>
   * This object can be referenced in a template string for <see cref="P:Empori.Core.Attributes.OptionsDefinitionAttribute.FormatString" /> for relation values using the <c>$</c> prefix.
   * </para><para>Example:</para>
   * ```
   * 
   * Context = new { title = "test" };
   * 
   * ...
   * 
   * [OptionsDefinition(FormatString = "<strong>{$BaseModelId.title}</strong>")]
   * public sealed class RowBaseModel
   * {
   * [OptionsProperty("Base Model", RelationType = typeof(BaseModelRelation))]
   * public int? BaseModelId { get; set; }
   * }
   * 
   * ```
   */
  context?: any | null;
}

/**
 * Type of list style to use for relation values.
 */
export enum RelationListStyle {

  /**
   * A single or a multi select dropdown list.
   */
  dropdown = 0,

  /**
   * A list of radio buttons or a list of checkboxes for multi select.
   */
  buttons = 1
}

export interface MenuOptionsItem {
  title: string;
  key: string;
  icon: string;
  view: string;
  viewData: { [key: string]: any };
  subMenu: MenuOptionsItem[];
}

export interface TableWindowQuery {
  tableKey: string;
  searchText: string;
  skip?: number;
  take?: number;
  sorting: TableSort;
  filters: TableFilter[];
}

export interface TableSort {
  column: string;
  descending: boolean;
}

export interface TableFilter {
  column: string;
  value: string;
  operator: TableFilterOperator;
}

export enum TableFilterOperator {
  equals = 0,
  notEquals = 1,
  greaterThan = 2,
  greaterThanOrEquals = 3,
  lessThan = 4,
  lessThanOrEquals = 5,
  contains = 6,
  startsWith = 7,
  endsWith = 8
}

/**
 * Base implementation of @see Empori.Core.Configuration.Table.ITableActionQuery.
 */
export interface TableActionQuery {

  /**
   * @inheritdoc 
   */
  tableKey: string;

  /**
   * @inheritdoc 
   */
  actionName: string;

  /**
   * @inheritdoc 
   */
  selectedIds: any[];

  /**
   * @inheritdoc 
   */
  deselectedIds: any[];

  /**
   * @inheritdoc 
   */
  selectedAll: boolean;

  /**
   * @inheritdoc 
   */
  model: any;

  /**
   * @inheritdoc 
   */
  searchText: string;

  /**
   * @inheritdoc 
   */
  filters: TableFilter[];
}

export interface TableResponse {
  key: string;
  title: string;
  icon: string;
  idColumn: string;
  editable: boolean;
  selectable: boolean;
  defaultSorting: TableSort;
  columns: TableColumn[];
  actions: TableAction[];
  definitions: ObjectDefinition[];
  properties: OptionsProperty[];
}

export interface TableColumn {
  key: string;
  title: string;
  type: TableColumnType;
  hide: boolean;
  options: { [key: string]: any };
  sortable: boolean;
  sortBy: string;
  filterable: boolean;
}

export enum TableColumnType {
  text = 0,
  integer = 1,
  decimal = 2,
  dateTime = 3,
  boolean = 4,
  enum = 5
}

/**
 * An action that can be executed on a table.
 */
export interface TableAction {

  /**
   * Gets or sets the key identifier of the action.
   */
  key: string;

  /**
   * Gets or sets the name of the action.
   */
  name: string;

  /**
   * Gets or sets an icon. Supports font-awesome icons.
   */
  icon: string;

  /**
   * Gets or sets the action type.
   */
  actionType: TableActionType;

  /**
   * Gets or sets the confirmation message.
   */
  comfirmMessage: string;

  /**
   * Gets or sets an optional CSS style for the action button.
   */
  style: string;

  /**
   * Gets or sets a value indicating whether the action is visible.
   */
  visible: boolean;

  /**
   * Gets or sets the sort order relative to other actions.
   */
  sortOrder: number;
}

export enum TableActionType {

  /**
   * A standalone action that does not require a row to be selected.
   */
  standalone = 0,

  /**
   * An action that requires at least one row to be selected.
   */
  oneOrMore = 1
}

export interface TableActionRunResult {
  success: boolean;
  value: any;
}

export interface TableActionResult {
  message: string;
  status: TableActionStatus;
  schema: OptionsSchema;
}

export enum TableActionStatus {
  info = 0,
  success = 1,
  error = 2,
  options = 10,
  actionResult = 11
}

export interface OptionsSchema {
  model: any;
  definitions: ObjectDefinition[];
  properties: OptionsProperty[];
}

export interface BackofficeViewRenderResult {
  html: string;
}

export interface JobDescriptionResponse {
  jobDescriptions: JobDescription[];
  latestRuns: ScheduledJobRun[];
}

/**
 * General model for all types of job descriptions.
 */
export interface JobDescription {
  active: boolean;
  lastRun?: string;
  nextOccurrence?: string;
  name: string;
  entity: ScheduledJob;
  currentRunEntity: ScheduledJobRun;
  status: JobReportStatus;
}

/**
 * Job reporter status for a job task during its execution.
 */
export enum JobReportStatus {

  /**
   * The job task is idling and waiting to be ran.
   */
  idle = 0,

  /**
   * The job task is currently running.
   */
  running = 1
}

export interface JobTypesResponse {
  types: JobTypeModel[];
}

export interface JobTypeModel {
  typeName: string;
  name: string;
}

/**
 * Job reporter event data from the reporter channel.
 */
export interface JobReporterEvent {

  /**
   * Gets or sets scheduled job entity id.
   */
  entityId: number;

  /**
   * Gets or sets progress value.
   */
  progress: number;

  /**
   * Gets or sets status text.
   */
  text: string;

  /**
   * Gets or sets job reporter status.
   */
  status: JobReportStatus;

  /**
   * Gets or sets internal job status that may be used to report metadata status to listeners.
   */
  metadataJobStatus?: MetadataJobStatus;
}

/**
 * Metadata job status.
 */
export enum MetadataJobStatus {

  /**
   * Job task is removed and should be propagated to listeners.
   */
  remove = 0,

  /**
   * Job task is updated and should be propagated to listeners.
   */
  update = 1,

  /**
   * Job task is created and should be propagated to listeners.
   */
  create = 2
}

/**
 * Job reporter status summary of individual job tasks.
 */
export interface JobReporterStatusSummary {

  /**
   * Job task events by entity id.
   */
  jobEvents: { [key: number]: JobReporterEvent };
}

export interface JobDescriptionUpdateRequest {
  model: JobTaskModel;
}

export interface JobTaskModel {

  /**
   * Scheduled job entity id.
   */
  entityId: number;

  /**
   * Given name of this job task.
   */
  name: string;

  /**
   * Cron timing expression.
   */
  cron: string;

  /**
   * Cron time zone in IANA id. UTC if `null`.
   */
  cronTimeZoneIana: string;

  /**
   * Decide whether this job task is active or not.
   */
  active: boolean;

  /**
   * Type name of this job task.
   */
  type: string;
}

export interface JobRunLogsResponse {
  run: ScheduledJobRun;
  logs: ScheduledJobRunLog[];
}

export interface JobRunsResponse {
  runs: ScheduledJobRun[];
}

export interface EventLog {
  message: string;
  exceptionMessage: string;
  exceptionStackTrace: string;
  exceptionSource: string;
}

export interface RichTextConfiguration {
  editor: any;
  blocks: RichTextBlockConfiguration[];
  useLiveEditing: boolean;
  overlaySize: string;
  hideLabel: boolean;
  mediaParentId: string;
  ignoreUserStartNodes: boolean;
}

export interface RichTextBlockConfiguration {
  backgroundColor: string;
  iconColor: string;
  thumbnail: string;
  contentElementTypeKey: string;
  settingsElementTypeKey?: string;
  view: string;
  stylesheet: string;
  label: string;
  editorSize: string;
  forceHideContentEditorInOverlay: boolean;
  displayInline: boolean;
}

/**
 * Umbraco search result response
 */
export interface SearchResultResponse {

  /**
   * Total count of the records received
   */
  totalCount: number;

  /**
   * Search term which was performed search on
   */
  searchTerm: string;

  /**
   * Search response item list
   */
  results: SearchResultItem[];
}

export interface SearchResultItem {
  title: string;
  description: string;

  /**
   * Url of the published content or url to media
   */
  url: string;
  id: string;

  /**
   * Content type whether a page or media
   */
  contentType: string;
}

export interface ImportPreviewResult {
  rows: HeaderRow[];
  sheetNames: string[];
}

/**
 * Header row of a spreadsheet.
 */
export interface HeaderRow {

  /**
   * Columns (cells) of the header row.
   */
  columns: HeaderCellValue[];
}

/**
 * A cell value in the header row.
 */
export interface HeaderCellValue {

  /**
   * @inheritdoc 
   */
  index: number;

  /**
   * String value of the cell.
   */
  value: string;

  /**
   * Type of the cell.
   */
  cellType: SpreadsheetCellType;
}

export enum SpreadsheetCellType {
  unknown = -1,
  numeric = 0,
  string = 1,
  formula = 2,
  blank = 3,
  boolean = 4,
  error = 5
}

export interface ImportRunRequest {
  keys: string[];
  targetType: string;
  sheetIndex?: number;
  skipRows?: number;
  mappedFields: string[];
  mapperOptions: MapperOptions;
}

export enum MapperOptions {
  auto = 0,
  manual = 1
}

export interface ImportUploadResult {
  files: ImportFile[];
}

/**
 * A temporarily stored file to import.
 */
export interface ImportFile {

  /**
   * Gets or sets the cache key of the file.
   */
  key: string;

  /**
   * Gets or sets the temporary name of the file.
   */
  tempName: string;

  /**
   * Gets or sets the original file name.
   */
  fileName: string;

  /**
   * Gets or sets the content type of the file.
   */
  contentType: string;

  /**
   * Gets or sets the length of the file.
   */
  length: number;
}

export interface ImportTargetsResponse {
  targetTypes: ImportTargetType[];
}

export interface ImportTargetType {
  type: string;
  name: string;
  description: string;
  fields: ImportTargetField[];
}

export interface ImportTargetField {
  key: string;
  name: string;
  required: boolean;
}

export interface ImportRunResponse {
  started: boolean;
  message: string;
}

export interface ImportHistoryResponse {
  jobs: ScheduledJob[];
  jobRuns: ScheduledJobRun[];
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IForm
 */
export interface Form extends EntityBase {

  /**
   * @inheritdoc 
   */
  name: string;
  inputs: InputItem[];
  settings: FormSettings;
}

export interface FormSettings extends BaseOptions {
  submitLabel: string;

  /**
   * Page node to redirect to after form has been posted. See also @see Empori.Core.Middlewares.FormPostMiddleware.
   */
  redirectPageNode: BaseNodePickerOption;

  /**
   * File size in megabytes (MiB).
   */
  maxUploadFileSize?: number;
  adminMailOptions: AdminEmailOptions;
  spamProtectionOptions: FormSpamProtectionOptions;
}

/**
 * Class contains the bare-minumum fields required for NodePicker property-editor to work.
 * Can be extended with all the fields listed in @see Empori.Core.Configuration.PropertyEditors.BaseNodePickerOption
 */
export interface BaseNodePickerOption {
  nodeId: number;
  name: string;
  icon: string;
}

export interface AdminEmailOptions extends MailToOptions {
  sendAdminEmail: GlobalNoYes;
  attachUploadedFiles: GlobalNoYes;
}

/**
 * No/yes override for a global @see System.Boolean option.
 */
export enum GlobalNoYes {

  /**
   * Use global option.
   */
  global = 0,

  /**
   * No.
   */
  no = 1,

  /**
   * Yes.
   */
  yes = 2
}

/**
 * Options for an email message with receiver address, sender address, template and subject.
 */
export interface MailToOptions extends MailOptions {

  /**
   * @inheritdoc 
   */
  toEmail: string;
}

/**
 * Options for an email message with sender address, template and subject.
 */
export interface MailOptions {

  /**
   * @inheritdoc 
   */
  fromEmail: string;

  /**
   * @inheritdoc 
   */
  template: string;

  /**
   * @inheritdoc 
   */
  title: string;
}

/**
 * emporicaptcha/turnstile override for a global @see System.Boolean option.
 */
export enum FormSpamProtectionOptions {

  /**
   * Use global option.
   */
  global = 0,

  /**
   * No.
   */
  none = 1,

  /**
   * Empori Captcha.
   */
  emporiCaptcha = 2,

  /**
   * 3rd Party Turnstile.
   */
  turnstile = 3
}

/**
 * Base abstract class of a configurable @see Microsoft.Extensions.Options.IOptions`1 in the backoffice.
 */
export interface BaseOptions {
}

export interface EntityBase extends BeingDirtyBase {
  id: number;
  key: string;
  createDate: string;
  updateDate: string;
  deleteDate?: string;
  hasIdentity: boolean;
}

export interface BeingDirtyBase {
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPost
 */
export interface FormPost extends EntityBase {

  /**
   * @inheritdoc 
   */
  formId: number;

  /**
   * @inheritdoc 
   */
  pageId?: number;
  pageCulture: string;

  /**
   * @inheritdoc 
   */
  ip: string;

  /**
   * @inheritdoc 
   */
  created: string;
  status: number;

  /**
   * @inheritdoc 
   */
  postData: FormPostData[];

  /**
   * @inheritdoc 
   */
  postFiles: FormPostFile[];
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPostData
 */
export interface FormPostData extends EntityBase {

  /**
   * @inheritdoc 
   */
  formPostId: number;

  /**
   * @inheritdoc 
   */
  name: string;

  /**
   * @inheritdoc 
   */
  value: string;
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPostFile
 */
export interface FormPostFile extends EntityBase {

  /**
   * @inheritdoc 
   */
  formPostId: number;

  /**
   * @inheritdoc 
   */
  fieldName: string;

  /**
   * @inheritdoc 
   */
  fileName: string;

  /**
   * @inheritdoc 
   */
  length: number;

  /**
   * @inheritdoc 
   */
  contentType: string;

  /**
   * @inheritdoc 
   */
  storageName: string;
}

export interface ScheduledJob extends EntityBase {
  active: boolean;
  lastRun: string;
  name: string;
  type: string;
  cron: string;
  cronTimeZoneIana: string;
  category: JobCategory;
}

/**
 * Job type category.
 */
export enum JobCategory {

  /**
   * Default recurring job.
   */
  recurring = 0,

  /**
   * Import job.
   */
  import = 10
}

export interface ScheduledJobRun extends EntityBase {
  jobId: number;
  result: JobRunResult;
  startDate: string;
  endDate?: string;
}

/**
 * Job run result.
 */
export enum JobRunResult {

  /**
   * Default idle result.
   */
  idle = 0,

  /**
   * Run is currently running.
   */
  running = 1,

  /**
   * Run is completed.
   */
  complete = 2,

  /**
   * Run is stopped.
   */
  stopped = 3,

  /**
   * Run has an error.
   */
  error = 4
}

export interface ScheduledJobRunLog extends EntityBase {
  runId: number;
  level: LogLevel;
  message: string;
}

export enum LogLevel {
  trace = 0,
  debug = 1,
  information = 2,
  warning = 3,
  error = 4,
  critical = 5,
  none = 6
}

export interface TableWindow {
  rows: any[];
  totalCount?: number;
}

